import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";
import type { AlwaysAiUser } from "../../../shared/api-types";
import type { NotificationListItem } from "../../../shared/notification-list";
import { NotFound } from "@/components/not-found";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import {
  NotificationList,
  NotificationsHeading,
  type NotificationAppointmentLinkProps,
} from "@/components/v2/notification-list-parts";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { useShellFrame } from "@/components/v2/shell-layout";
import { authenticatedApi } from "@/lib/api";
import { deviceTimezone } from "@/lib/appointments";
import {
  hasOlderNotifications,
  notificationItems,
  notificationsPageState,
} from "@/lib/notifications-client";
import { can } from "@/lib/permissions";
import { NOTIFICATIONS_FLAG, useFeatures } from "@/lib/use-features";
import { useMarkNotificationRead, useNotificationList } from "@/lib/use-notifications";

// /notifications (NO-830): the RD's own in-app alerts, in the single-rail frame
// only. Blank while the flags and the viewer load; the router's 404 for
// anyone the rail would not show the Notifications row to.

function useCurrentUser() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ["current-user"],
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });
}

export function NotificationsPage(): ReactElement | null {
  const frame = useShellFrame();
  const features = useFeatures();
  const me = useCurrentUser();
  const user = me.data?.user;
  const state = notificationsPageState({
    frame,
    featuresLoading: features.isPending || me.isPending,
    notificationsEnabled: features.data?.data[NOTIFICATIONS_FLAG] === true,
    canReadOwnAppointments: user ? can(user, "appointments:read:own") : false,
  });

  if (state === "blank") return null;

  if (state === "not_found") return <NotFound />;

  return <NotificationsContent />;
}

function renderAppointmentLink({
  appointmentId,
  className,
  onClick,
  dataAttributes,
  children,
}: NotificationAppointmentLinkProps): ReactElement {
  return (
    <Link
      to="/appointments/$appointmentId"
      params={{ appointmentId }}
      className={className}
      onClick={onClick}
      {...dataAttributes}
    >
      {children}
    </Link>
  );
}

function NotificationsContent(): ReactElement {
  const list = useNotificationList();
  const markRead = useMarkNotificationRead();

  const open = (item: NotificationListItem): void => {
    if (item.readAt === null) markRead.mutate(item);
  };

  return (
    <div className="mx-auto max-w-[880px] px-6 pb-12 pt-8">
      <BreadcrumbSetter segments={[{ label: "Notifications" }]} />
      <NotificationsHeading />
      {list.isError ? (
        <QueryErrorState
          error={list.error}
          notFoundText="Notifications are not available"
          onRetry={() => void list.refetch()}
          className="mt-10"
        />
      ) : list.data ? (
        <NotificationList
          items={notificationItems(list.data)}
          timezone={deviceTimezone()}
          onOpen={open}
          renderAppointmentLink={renderAppointmentLink}
          older={{
            hasOlder: hasOlderNotifications(list.data),
            loadingOlder: list.isFetchingNextPage,
            onShowOlder: () => void list.fetchNextPage(),
          }}
        />
      ) : null}
    </div>
  );
}
